import { useState } from 'react'
import { ArchiveX } from 'lucide-react'
import { useTranslation } from '@kostbar/i18n'
import { useUnpublishMenu } from '../hooks/use-menu-mutations'
import type { Menu } from '@/api/types'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'

interface UnpublishMenuButtonProps {
  menu: Menu
}

export function UnpublishMenuButton({ menu }: UnpublishMenuButtonProps) {
  const { t: tMenus } = useTranslation('menus')
  const [showConfirm, setShowConfirm] = useState(false)
  const unpublishMenu = useUnpublishMenu()

  const handleUnpublish = async () => {
    try {
      await unpublishMenu.mutateAsync(menu.id)
      setShowConfirm(false)
    } catch (error) {
      // Error is handled by the mutation hook
      setShowConfirm(false)
    }
  }

  return (
    <>
      <Button size="sm" variant="outline" onClick={() => setShowConfirm(true)}>
        <ArchiveX className="w-4 h-4 mr-2" />
        {tMenus('unpublish.title')}
      </Button>

      <Dialog open={showConfirm} onOpenChange={setShowConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{tMenus('unpublish.title')}</DialogTitle>
            <DialogDescription>
              {tMenus('unpublish.description')}
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-lg border p-4 bg-muted/50">
            <p className="text-sm text-muted-foreground">
              {tMenus('unpublish.confirm', { name: menu.name })}
            </p>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowConfirm(false)}
              disabled={unpublishMenu.isPending}
            >
              {tMenus('unpublish.cancel')}
            </Button>
            <Button
              onClick={handleUnpublish}
              disabled={unpublishMenu.isPending}
            >
              {unpublishMenu.isPending
                ? tMenus('unpublish.unpublishing')
                : tMenus('unpublish.unpublish')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  )
}
